import sendForm from "@partials/UI/form/modules/sendForm/sendForm";
import { validateField, clearErrorsForInput } from "@partials/UI/form/modules/sendForm/validateForm";
import validateForm from "@partials/UI/form/modules/sendForm/validateForm";

const RECAPTCHA_KEY = process.env.RECAPTCHA_SITE_KEY;
const DEBOUNCE_DELAY = 500;
const submittingForms = new WeakSet();

// Debounced валидатор
const debouncedValidate = debounce(input => {
    if (input.value.trim()) validateSingleField(input);
    else clearErrorsForInput(input);
}, DEBOUNCE_DELAY);

// ОТПРАВКА ФОРМЫ
async function submitForm(form, event = null) {
    if (!form || submittingForms.has(form)) return;

    if (event) event.preventDefault();
    submittingForms.add(form);

    try {
        if (!validateForm(form)) return false;

        if (!RECAPTCHA_KEY || RECAPTCHA_KEY.length < 20) {
            throw new Error("reCAPTCHA ключ невалидный");
        }

        await waitForRecaptcha();
        const token = await getRecaptchaToken();
        return await sendForm(form, token);
    } catch (error) {
        return false;
    } finally {
        submittingForms.delete(form);
    }
}

// RECAPTCHA
const waitForRecaptcha = (maxWait = 15000) =>
    new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
            if (typeof grecaptcha !== "undefined" && grecaptcha.ready) {
                grecaptcha.ready(resolve);
            } else if (Date.now() - start > maxWait) {
                reject(new Error("reCAPTCHA timeout"));
            } else {
                setTimeout(check, 150);
            }
        };
        check();
    });

const getRecaptchaToken = () =>
    new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
            grecaptcha
                .execute(RECAPTCHA_KEY, { action: "submit" })
                .then(token => (token ? resolve(token) : reject(new Error("Empty token"))))
                .catch(reject);
        });
    });

// ВАЛИДАЦИЯ ПОЛЕЙ
function validateSingleField(input) {
    const fieldName = input.dataset.field;
    const fieldConfig = FORM_FIELDS?.fields?.[fieldName];

    if (fieldConfig?.validation) {
        const isValid = validateField(input, fieldConfig.validation);
        updateFieldUI(input, isValid, fieldConfig.error);
        return isValid;
    }
    return true;
}

function updateFieldUI(input, isValid, errorMessage) {
    const parent = input.parentNode;
    const errorElement = input.nextElementSibling;

    if (isValid) {
        parent.classList.remove("error");
        if (errorElement) errorElement.textContent = "";
    } else {
        parent.classList.add("error");
        if (errorElement) errorElement.textContent = errorMessage;
    }
}

// ОБРАБОТЧИКИ СОБЫТИЙ
function handleBlur(event) {
    if (event.target.value.trim()) validateSingleField(event.target);
}

function handleFocus(event) {
    clearErrorsForInput(event.target);
}

function handleInput(event) {
    debouncedValidate(event.target);
}

// ИНИЦИАЛИЗАЦИЯ
function initValidation(form) {
    form.querySelectorAll("input[data-field], textarea[data-field]").forEach(input => {
        input.addEventListener("blur", handleBlur);
        input.addEventListener("focus", handleFocus);
        input.addEventListener("input", handleInput);
    });

    form.addEventListener("submit", e => {
        e.preventDefault(); // Предотвращаем отправку СРАЗУ!
        submitForm(form, e);
    });
}

function initValidationForNewForms(container = document) {
    container.querySelectorAll(".form").forEach(initValidation);
}

// DEBOUNCE
function debounce(func, wait) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

// АВТОИНИЦИАЛИЗАЦИЯ
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initValidationForNewForms());
} else {
    initValidationForNewForms();
}

// НАБЛЮДАТЕЛЬ ЗА ДИНАМИЧЕСКИМИ ФОРМАМИ
new MutationObserver(mutations => {
    mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
                if (node.classList?.contains("form")) initValidation(node);
                initValidationForNewForms(node);
            }
        });
    });
}).observe(document.body, { childList: true, subtree: true });

// ЗАЩИТА ОТ КРАША - блокируем ВСЕ unhandled rejections
window.addEventListener("unhandledrejection", event => {
    event.preventDefault();
});

// ЭКСПОРТ
export const onSubmitForm = form => submitForm(form);
export { initValidationForNewForms };
